// range/classify.ts - the combo classifier: the input to ActionModel evaluation (ENGINE_DESIGN §4.2,
// §4.3). Given a 2-card combo and the current board, it returns the combo's HandClass (one of the 17
// HAND_CLASS_ORDER values the archetype ActionModel is keyed on) plus its DrawFlags. This is the
// bridge that lets ONE table drive two uses: narrow() classifies a villain combo → looks up that
// class's continue/raise probability; the scenario engine (S9) classifies the villain's actual hand
// the same way to sample its action. Same classifier, same keys, provably consistent.
//
// Design choices (the judgment core - no mechanical oracle, so documented explicitly):
//   • A made hand outranks a draw: HAND_CLASS_ORDER puts draws below made pairs, so a paired combo is
//     classified by its pair even when it also carries a flush draw. The draw still surfaces in
//     drawFlags for rules/bucketing, but the single HandClass key follows the made-hand ladder.
//   • Pair sub-tiering keys off WHICH board rank is paired: top / second / lower board rank, with the
//     other hole card as kicker. Pocket pairs classify by where the pair sits vs the board.
//   • Draw strength: flush draw, open-ended, double-gutshot, or a combo (flush+straight) draw ⇒
//     strong_draw; a lone gutshot or two bare overcards ⇒ weak_draw; an unpaired ace ⇒ ace_high;
//     nothing ⇒ air.
import { HAND_CLASS_ORDER, } from '@postflop/schema';
import { rankOf, suitOf } from '../core/cards';
import { handCategory, HandCategory } from '../core/eval';
/** Kicker rank at/above which a top pair is "top kicker" (an ace) vs merely "good" (J/Q/K). */
const ACE_RANK = 12;
const GOOD_KICKER_MIN = 9; // Jack - J,Q,K are "good" kickers; T and below are "weak".
/** Distinct board ranks, high → low. */
const distinctBoardRanksDesc = (board) => [...new Set(board.map((c) => rankOf(c)))].sort((a, b) => b - a);
/**
 * Straight-completing ranks for a set of distinct ranks: every rank r whose addition yields five in a
 * row. The Ace is counted both high (12) and low (-1) so wheels register. Returns the sorted list.
 */
const straightCompleters = (ranks) => {
    const present = new Set(ranks);
    if (present.has(ACE_RANK))
        present.add(-1);
    const completers = [];
    for (let r = -1; r <= 12; r++) {
        if (present.has(r))
            continue;
        const withR = new Set(present);
        withR.add(r);
        // any 5-window fully covered?
        let made = false;
        for (let lo = -1; lo <= 8 && !made; lo++) {
            let all = true;
            for (let v = lo; v < lo + 5; v++)
                if (!withR.has(v)) {
                    all = false;
                    break;
                }
            if (all)
                made = true;
        }
        if (made)
            completers.push(r === -1 ? ACE_RANK : r);
    }
    return [...new Set(completers)].sort((a, b) => a - b);
};
/** Detect draws for a hole+board combo (flop/turn only - a 5-card board can't be drawing). */
const detectDraws = (hole, board) => {
    const flags = [];
    if (board.length >= 5)
        return flags; // river: no draws, only made hands
    if (board.length < 3)
        return flags; // preflop handled by the caller
    const holeRanks = hole.map((c) => rankOf(c));
    const boardRanks = board.map((c) => rankOf(c));
    const holeSuits = hole.map((c) => suitOf(c));
    const allSuits = [...holeSuits, ...board.map((c) => suitOf(c))];
    // ── flush draws ─────────────────────────────────────────────────────────────
    const suitCounts = [0, 0, 0, 0];
    for (const s of allSuits)
        suitCounts[s]++;
    let flushDraw = false;
    for (let s = 0; s < 4; s++) {
        if (suitCounts[s] === 4 && holeSuits.includes(s)) {
            flushDraw = true;
            // nut flush draw = we hold the Ace of the drawing suit.
            const holdsAceOfSuit = hole.some((c) => suitOf(c) === s && rankOf(c) === ACE_RANK);
            flags.push(holdsAceOfSuit ? 'nut_flush_draw' : 'flush_draw');
        }
        else if (board.length === 3 && suitCounts[s] === 3 && holeSuits.includes(s)) {
            flags.push('backdoor_flush');
        }
    }
    // ── straight draws (only when the hole cards contribute) ───────────────────────
    const poolRanks = new Set([...holeRanks, ...boardRanks]);
    const boardOnly = new Set(boardRanks);
    const poolCompleters = straightCompleters(poolRanks);
    const boardCompleters = straightCompleters(boardOnly);
    // completers the hole cards create that the board alone did not → the combo's own straight outs.
    const ownCompleters = poolCompleters.filter((r) => !boardCompleters.includes(r));
    if (ownCompleters.length >= 2) {
        // 8-out draw. Consecutive completers (both ends of a run) ⇒ OESD; otherwise double gutshot.
        const consecutive = ownCompleters.some((r, i) => i > 0 && r - ownCompleters[i - 1] === 1);
        const span = ownCompleters[ownCompleters.length - 1] - ownCompleters[0];
        flags.push(consecutive || span <= 1 ? 'oesd' : 'double_gutshot');
    }
    else if (ownCompleters.length === 1) {
        flags.push('gutshot');
    }
    // combo draw: a flush draw AND a straight draw together (≥ ~12 outs).
    if (flushDraw && (flags.includes('oesd') || flags.includes('gutshot') || flags.includes('double_gutshot'))) {
        flags.push('combo_draw');
    }
    // ── overcards: both hole cards clean over the board, unpaired ──────────────────
    const topBoard = Math.max(...boardRanks);
    const bothOver = holeRanks.every((r) => r > topBoard);
    const pairsBoard = holeRanks.some((r) => boardRanks.includes(r));
    const isPocketPair = holeRanks[0] === holeRanks[1];
    if (bothOver && !pairsBoard && !isPocketPair)
        flags.push('overcards');
    return flags;
};
/** Classify a one-pair combo into its ladder tier (overpair / top/second/lower pair + kicker). */
const classifyPair = (hole, board) => {
    const holeRanks = hole.map((c) => rankOf(c));
    const boardDesc = distinctBoardRanksDesc(board);
    const topBoard = boardDesc[0];
    const secondBoard = boardDesc[1] ?? -1;
    // Pocket pair: classify by where the pair sits relative to the board.
    if (holeRanks[0] === holeRanks[1]) {
        const pr = holeRanks[0];
        if (pr > topBoard)
            return 'overpair';
        if (pr > secondBoard)
            return 'second_pair_good_kicker'; // under only the top board card
        return 'weak_pair';
    }
    // One hole card pairs a board rank. Find which, and the other hole card is the kicker.
    const [h0, h1] = holeRanks;
    const h0Pairs = board.some((c) => rankOf(c) === h0);
    const pairedRank = h0Pairs ? h0 : h1;
    const kicker = h0Pairs ? h1 : h0;
    if (pairedRank === topBoard) {
        if (kicker === ACE_RANK)
            return 'top_pair_top_kicker';
        if (kicker >= GOOD_KICKER_MIN)
            return 'top_pair_good_kicker';
        return 'top_pair_weak_kicker';
    }
    if (pairedRank === secondBoard) {
        return kicker >= GOOD_KICKER_MIN ? 'second_pair_good_kicker' : 'weak_pair';
    }
    return 'weak_pair';
};
/** Classify an unpaired (high-card) combo by its draw strength / high-card value. */
const classifyUnpaired = (hole, drawFlags) => {
    const strong = ['flush_draw', 'nut_flush_draw', 'oesd', 'double_gutshot', 'combo_draw'];
    if (drawFlags.some((f) => strong.includes(f)))
        return 'strong_draw';
    if (drawFlags.includes('gutshot'))
        return 'weak_draw';
    const hasAce = hole.some((c) => rankOf(c) === ACE_RANK);
    if (hasAce)
        return 'ace_high';
    if (drawFlags.includes('overcards'))
        return 'weak_draw';
    return 'air';
};
/**
 * Classify a combo against the current board into its HandClass + draw flags.
 * Board length 3–5 uses the full made-hand evaluator; board length 0 (preflop) uses a coarse
 * pocket-pair / high-card read, since narrowing and bucketing are postflop concepts.
 */
export const classifyCombo = (hole, board) => {
    // Preflop: no board to make a hand against.
    if (board.length < 3) {
        const [r0, r1] = hole.map((c) => rankOf(c));
        if (r0 === r1)
            return { handClass: r0 >= 9 ? 'overpair' : 'weak_pair', drawFlags: [] };
        const hasAce = r0 === ACE_RANK || r1 === ACE_RANK;
        return { handClass: hasAce ? 'ace_high' : 'air', drawFlags: [] };
    }
    const drawFlags = detectDraws(hole, board);
    const cat = handCategory(hole, board);
    let handClass;
    switch (cat) {
        case HandCategory.StraightFlush:
        case HandCategory.RoyalFlush:
            handClass = 'straight_flush';
            break;
        case HandCategory.FourOfAKind:
            handClass = 'quads';
            break;
        case HandCategory.FullHouse:
            handClass = 'full_house';
            break;
        case HandCategory.Flush:
            handClass = 'flush';
            break;
        case HandCategory.Straight:
            handClass = 'straight';
            break;
        case HandCategory.ThreeOfAKind:
            handClass = 'set';
            break;
        case HandCategory.TwoPair:
            handClass = 'two_pair';
            break;
        case HandCategory.OnePair:
            handClass = classifyPair(hole, board);
            break;
        default:
            handClass = classifyUnpaired(hole, drawFlags);
            break;
    }
    return { handClass, drawFlags };
};
/** Rank of a HandClass on the HAND_CLASS_ORDER ladder (0 = weakest). */
export const classRank = (hc) => HAND_CLASS_ORDER.indexOf(hc);
//# sourceMappingURL=classify.js.map